import React, { ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { Box } from '@chakra-ui/react';
import { ApolloError } from '@apollo/client';
import Heading from '#coreComponents/Heading';
import { usePermissions } from '#core/Permissions';

const errorSuffix =
  'Our team has been notified and is working to resolve the issue.';

export const getErrorMessage = (
  id: string,
  error: Error | undefined,
  isDeveloper: boolean,
): string | ReactNode => {
  if (error instanceof ApolloError) {
    const apolloError = error as ApolloError;
    if (apolloError.networkError) {
      const { networkError } = apolloError;
      // @ts-ignore - result exists on the object but isnt exposed as a type by Apollo
      const errors = (networkError?.result?.errors || []) as {
        message: string;
      }[];
      const detailedError =
        errors.length > 0
          ? `${errors.map(({ message }) => message).join(', ')}`
          : '';
      return isDeveloper ? (
        <>
          <Heading size="md">{id}: Network Error</Heading>
          <p>{detailedError || networkError.message}</p>
        </>
      ) : (
        `There was an error connecting to the server. ${errorSuffix}`
      );
    }
    if (apolloError.graphQLErrors.length > 0) {
      return isDeveloper ? (
        <>
          <Heading size="md">{id}: GraphQL Error</Heading>
          <p>{JSON.stringify(apolloError.graphQLErrors)}</p>
        </>
      ) : (
        `There was an error when querying for data. ${errorSuffix}`
      );
    }
  }
  return isDeveloper
    ? `${id}: ${error?.message || 'No Error'}`
    : `Something went wrong. ${errorSuffix}`;
};

const ErrorMessageLite: React.FC<FallbackProps & { id: string }> = ({
  id,
  error,
}) => {
  const { hasRole } = usePermissions();
  const isDeveloper = hasRole('Developer');
  return (
    <Box
      padding={4}
      borderRadius={2}
      p="0.3rem 0.2rem"
      color="red.600"
      fontWeight="bold"
    >
      {getErrorMessage(id, error, isDeveloper)}
    </Box>
  );
};

export default ErrorMessageLite;
